.root {
  --mono:
    ui-monospace, 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono',
    monospace;
  --indent: 16px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--surface-1);
  color: var(--on-surface);
  font-family: var(--font-sans);
  letter-spacing: normal;
}

/* Toolbar: search with a match counter, then expand and collapse all. */
.toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px;
  border-bottom: 1px solid var(--border);
}
.search {
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 0 4px 0 10px;
  border-radius: 14px;
  background: var(--surface-2);
  cursor: text;
}
.searchIcon {
  flex: none;
  color: var(--on-surface-secondary);
}
.input {
  min-width: 0;
  flex: 1;
  height: 34px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--on-surface);
  font: inherit;
  font-size: var(--text-sm);
  outline: none;
}
.input::placeholder {
  color: var(--on-surface-secondary);
}
.input::-webkit-search-cancel-button {
  display: none;
}
.matches {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 0;
}
.count {
  min-width: 3.5ch;
  padding-inline: 4px;
  color: var(--on-surface-secondary);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.grow {
  flex: 1;
}
.divider {
  width: 1px;
  height: 18px;
  margin-inline: 4px;
  background: var(--border);
}
.tool {
  display: inline-grid;
  width: 34px;
  height: 34px;
  flex: none;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: var(--on-surface-secondary);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    color 160ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 160ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tool:disabled {
  cursor: default;
  opacity: 0.4;
}
.tool:active:not(:disabled) {
  background: var(--surface-2);
  color: var(--on-surface);
}
.search .tool {
  width: 26px;
  height: 26px;
  border-radius: 9px;
}
.search .tool:active:not(:disabled) {
  background: var(--surface-1);
}

/* Tree. Rows are one fixed height, so scrolling can aim at where a row will settle. */
.tree {
  position: relative;
  max-height: var(--json-max-height, 420px);
  padding: 6px;
  overflow: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  font-family: var(--font-mono);
  font-size: 13px;
  font-variant-ligatures: none;
  letter-spacing: 0;
  scrollbar-width: thin;
}
.row {
  position: relative;
  border-radius: 10px;
  cursor: default;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.highlight {
  position: absolute;
  inset: 1px 0;
  border-radius: 9px;
  background: var(--surface-2);
}
.line {
  position: relative;
  display: flex;
  height: 30px;
  min-width: 0;
  align-items: center;
  gap: 6px;
  padding-right: 6px;
  padding-left: calc(4px + var(--level, 0) * var(--indent));
  white-space: nowrap;
}
.row[data-kind='node'] .line {
  cursor: pointer;
}

.chevron {
  display: inline-grid;
  width: 14px;
  height: 14px;
  flex: none;
  place-items: center;
  color: var(--on-surface-secondary);
  transition:
    transform 100ms cubic-bezier(0.22, 1, 0.36, 1),
    color 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.chevron[data-open] {
  transform: rotate(90deg);
}
.dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
}
.row[data-active] .chevron[data-branch] {
  color: var(--on-surface);
}

.key {
  flex: none;
  max-width: 45%;
  overflow: hidden;
  color: var(--on-surface);
  text-overflow: ellipsis;
}
.index {
  flex: none;
  color: var(--on-surface-secondary);
  font-variant-numeric: tabular-nums;
}
.colon {
  flex: none;
  margin-left: -5px;
  color: var(--on-surface-secondary);
}
.summary {
  display: inline-flex;
  min-width: 0;
  align-items: baseline;
  gap: 8px;
  overflow: hidden;
}
.preview {
  min-width: 0;
  overflow: hidden;
  color: var(--on-surface-secondary);
  text-overflow: ellipsis;
}
.countLabel {
  flex: none;
  color: var(--on-surface-secondary);
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
}

/* Type colors lean on the semantic roles, so both themes stay legible. */
.value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.root[data-wrap-values] .line {
  height: auto;
  min-height: 30px;
  align-items: baseline;
  white-space: normal;
}

.root[data-wrap-values] .value {
  overflow: visible;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.value[data-type='string'] {
  color: var(--success);
}
.value[data-type='number'] {
  color: var(--primary);
  font-variant-numeric: tabular-nums;
}
.value[data-type='boolean'] {
  color: var(--warning);
}
.value[data-type='null'],
.value[data-type='other'] {
  color: var(--on-surface-secondary);
}

.mark {
  border-radius: 3px;
  background: color-mix(in oklab, var(--warning) 22%, transparent);
  color: inherit;
  box-shadow: 0 0 0 1px color-mix(in oklab, var(--warning) 22%, transparent);
  transition: background-color 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.mark[data-current] {
  background: color-mix(in oklab, var(--warning) 48%, transparent);
}

.more {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  padding-left: 20px;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
}
.moreLabel {
  color: var(--on-surface);
  font-weight: 500;
}
.row[data-kind='more'] {
  cursor: pointer;
}
.muted {
  color: var(--on-surface-secondary);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
}

/* Copy actions appear at the row's end over a soft fade, on hover or on the current row. */
/* Inset like the highlight, with its right corners, so the fade never shows a square edge past the row's rounded end. */
.actions {
  position: absolute;
  top: 1px;
  right: 0;
  bottom: 1px;
  display: flex;
  align-items: center;
  gap: 0;
  padding-right: 1px;
  padding-left: 24px;
  border-radius: 0 9px 9px 0;
  background: linear-gradient(to right, transparent, var(--surface-1) 22px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.row[data-active] .actions {
  background: linear-gradient(to right, transparent, var(--surface-2) 22px);
}
.action {
  position: relative;
  display: inline-grid;
  width: 26px;
  height: 26px;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--on-surface-secondary);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    color 160ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.action[data-state='done'] {
  color: var(--success);
}
.action[data-state='error'] {
  color: var(--error);
}
.action:active {
  background: color-mix(in oklab, var(--on-surface) 8%, transparent);
}
.actionGlyph {
  display: grid;
  place-items: center;
}
.row[data-active] .actions,
.row:focus-within .actions {
  opacity: 1;
  pointer-events: auto;
}

/* The current path, always readable in full through its copy action. */
.pathBar {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 14px;
  border-top: 1px solid var(--border);
}
.path {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--on-surface-secondary);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pathType {
  flex: none;
  color: var(--on-surface-secondary);
  font-size: var(--text-xs);
}
.pathCopy {
  width: 30px;
  height: 30px;
  border-radius: 10px;
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (hover: hover) and (pointer: fine) {
  .row:hover .actions {
    opacity: 1;
    pointer-events: auto;
  }
  /* Same inset and corner as the current-row highlight, so hover and selection share one shape. */
  .row:not([data-active]):hover {
    --row-hover: color-mix(in oklab, var(--on-surface) 3.5%, var(--surface-1));
  }
  .row:not([data-active]):hover::before {
    content: '';
    position: absolute;
    inset: 1px 0;
    border-radius: 9px;
    background: var(--row-hover);
    pointer-events: none;
  }
  .row:not([data-active]):hover .actions {
    background: linear-gradient(to right, transparent, var(--row-hover) 22px);
  }
  .row:hover .chevron[data-branch] {
    color: var(--on-surface);
  }
  .tool:hover:not(:disabled),
  .action:hover {
    background: var(--surface-2);
    color: var(--on-surface);
  }
  .search .tool:hover:not(:disabled) {
    background: var(--surface-1);
  }
  .action[data-state='done']:hover {
    color: var(--success);
  }
}

@media (max-width: 420px) {
  .tree {
    --indent: 12px;
  }
  .key {
    max-width: 40%;
  }
  .pathType {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chevron,
  .actions,
  .tool,
  .action,
  .mark {
    transition: none;
  }
}
